/**
 * Feature Access Service
 * Controls which features are available based on subscription status
 * All features require active subscription or trial - nothing is free
 */

import { subscriptionService, SubscriptionStatus } from './subscriptionService';
import { telegram } from './telegram';

export type FeatureId = 
  | 'voiceNav'           // Voice navigation & commands
  | 'aiAssistant'        // AI chat assistant
  | 'offlineCaching'     // Download counties for offline
  | 'ocdLayers'          // OCD incidents/pits/frac layers
  | 'crewManagement'     // My Crews feature
  | 'unlimitedFavorites' // More than 5 favorites
  | 'productionCharts'   // Well production data
  | 'calichemRoutes'     // Caliche routes (always free)
  | 'hazardReporting'    // Road hazard reports (always free)
  | 'basicSearch'        // Search facilities (always free)
  | 'basicMap';          // Map viewing (always free)

export interface FeatureConfig {
  id: FeatureId;
  name: string;
  description: string;
  icon: string;
  freeAccess: boolean;  // Available after trial expires
}

// Feature configuration - defines what's free vs premium
const FEATURE_CONFIG: Record<FeatureId, FeatureConfig> = {
  voiceNav: {
    id: 'voiceNav',
    name: 'Voice Navigation',
    description: 'Hands-free voice commands and turn-by-turn directions',
    icon: '🎙️',
    freeAccess: false  // Requires subscription
  },
  aiAssistant: {
    id: 'aiAssistant', 
    name: 'AI Assistant',
    description: 'Ask questions about wells, regulations, and get smart answers',
    icon: '🤖',
    freeAccess: false  // Requires subscription
  },
  offlineCaching: {
    id: 'offlineCaching',
    name: 'Offline Maps',
    description: 'Download entire counties for offline access in remote areas',
    icon: '📥',
    freeAccess: false
  },
  ocdLayers: {
    id: 'ocdLayers',
    name: 'OCD Activity Layers',
    description: 'View incidents, pits, and frac activity on the map',
    icon: '🏗️',
    freeAccess: false  // Requires subscription
  },
  crewManagement: {
    id: 'crewManagement',
    name: 'Crew Management',
    description: 'Create and manage field crews with location sharing',
    icon: '👷',
    freeAccess: false
  },
  unlimitedFavorites: {
    id: 'unlimitedFavorites',
    name: 'Unlimited Favorites',
    description: 'Save more than 5 favorite locations',
    icon: '⭐',
    freeAccess: false
  },
  productionCharts: {
    id: 'productionCharts',
    name: 'Production Charts',
    description: 'View oil, gas, and water production history for wells',
    icon: '📊',
    freeAccess: false
  },
  // Requires subscription - nothing is free after trial
  calichemRoutes: {
    id: 'calichemRoutes',
    name: 'Caliche Routes',
    description: 'Community-shared routes and photos for field navigation',
    icon: '🛤️',
    freeAccess: false  // Requires subscription
  },
  hazardReporting: {
    id: 'hazardReporting',
    name: 'Road Hazards',
    description: 'View and report road hazards',
    icon: '⚠️',
    freeAccess: false  // Requires subscription
  },
  basicSearch: {
    id: 'basicSearch',
    name: 'Facility Search',
    description: 'Search for wells, SWDs, rigs, and other facilities',
    icon: '🔍',
    freeAccess: false  // Requires subscription
  },
  basicMap: {
    id: 'basicMap',
    name: 'Map Navigation',
    description: 'View facilities on the map',
    icon: '🗺️',
    freeAccess: false  // Requires subscription
  }
};

// Analytics event types
export type FeatureAccessEvent = {
  featureId: FeatureId;
  action: 'access_granted' | 'access_denied' | 'upgrade_shown' | 'upgrade_clicked' | 'upgrade_dismissed';
  subscriptionStatus: SubscriptionStatus['status'];
  timestamp: number;
};

class FeatureAccessService {
  // @ts-ignore - kept for potential future use
  private cachedStatus: SubscriptionStatus | null = null;
  private analyticsQueue: FeatureAccessEvent[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Flush analytics every 30 seconds
    this.flushInterval = setInterval(() => this.flushAnalytics(), 30000);
  }

  /**
   * Get feature configuration
   */
  getFeatureConfig(featureId: FeatureId): FeatureConfig {
    return FEATURE_CONFIG[featureId];
  }

  /**
   * Get all premium features (for display in upgrade prompts)
   */
  getPremiumFeatures(): FeatureConfig[] {
    return Object.values(FEATURE_CONFIG).filter(f => !f.freeAccess);
  }

  /**
   * Get all free features (for display)
   */
  getFreeFeatures(): FeatureConfig[] {
    return Object.values(FEATURE_CONFIG).filter(f => f.freeAccess);
  }

  /**
   * Check if user can access a feature (without showing prompt)
   * ALL FEATURES UNLOCKED - No premium restrictions
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async canAccess(_featureId: FeatureId): Promise<boolean> {
    // All features are now free - no restrictions
    return true;
  }

  /**
   * Check access and show upgrade prompt if denied
   * ALL FEATURES UNLOCKED - Always returns true
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async checkAccessWithPrompt(_featureId: FeatureId): Promise<boolean> {
    // All features are now free - always grant access
    return true;
  }

  /**
   * DEPRECATED - Show gentle upgrade prompt for a feature
   * No longer used since all features are free
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showUpgradePrompt(_feature: FeatureConfig, _status: SubscriptionStatus) {
    // No-op - all features are free now
    return;
  }

  /**
   * Check if trial has expired (for showing banner)
   * Always returns false since all features are free
   */
  async isTrialExpired(): Promise<boolean> {
    return false;
  }

  /**
   * Check if user has ever had a subscription
   */
  async hasHadSubscription(): Promise<boolean> {
    const status = await subscriptionService.getStatus();
    return !status.canTrial || status.status !== 'none';
  }

  /**
   * Get subscription status summary for UI
   */
  async getStatusSummary(): Promise<{
    canAccessPremium: boolean;
    showTrialBanner: boolean;
    showExpiredBanner: boolean;
    trialDaysRemaining: number;
    statusText: string;
  }> {
    const status = await subscriptionService.getStatus();
    
    return {
      canAccessPremium: status.isSubscribed,
      showTrialBanner: status.isTrialing && status.trialDaysRemaining <= 3,
      showExpiredBanner: status.status === 'expired' || status.status === 'canceled',
      trialDaysRemaining: status.trialDaysRemaining,
      statusText: this.getStatusText(status)
    };
  }

  private getStatusText(status: SubscriptionStatus): string {
    switch (status.status) {
      case 'active':
        return 'Premium Active';
      case 'trialing':
        return `Trial: ${status.trialDaysRemaining} day${status.trialDaysRemaining !== 1 ? 's' : ''} left`;
      case 'expired':
        return 'Trial Expired';
      case 'canceled':
        return 'Subscription Canceled';
      case 'past_due':
        return 'Payment Past Due';
      default:
        return status.canTrial ? 'Start Trial' : 'Subscribe';
    }
  }

  /**
   * Flush analytics events to server
   */
  private async flushAnalytics() {
    if (this.analyticsQueue.length === 0) return;
    
    const events = [...this.analyticsQueue];
    this.analyticsQueue = [];
    
    try {
      await fetch('/api/analytics/features', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': telegram.getUserId() || '',
        },
        body: JSON.stringify({ events })
      });
    } catch (error) {
      // Re-queue failed events
      this.analyticsQueue.unshift(...events);
      console.error('[FeatureAccess] Failed to flush analytics:', error);
    }
  }

  /**
   * Cleanup on unmount
   */
  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flushAnalytics();
  }
}

export const featureAccess = new FeatureAccessService();
